import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchModule,
  fetchRecords,
  createRecord,
  updateRecord,
  deleteRecord,
  clearModule,
  exportUrl,
} from "@/lib/api";
import { compactNumber } from "@/lib/format";
import { toast } from "sonner";
import {
  Upload,
  Download,
  Plus,
  Trash2,
  Save,
  X,
  Pencil,
  Search,
  FileSpreadsheet,
  AlertTriangle,
  Sparkles,
  Paperclip,
  FolderOpen,
  Clock,
} from "lucide-react";
import UploadDialog from "@/components/UploadDialog";
import DataChart from "@/components/DataChart";
import SpdDetailModal from "@/components/SpdDetailModal";
import { computeOverdue } from "@/lib/overdue";

export default function ModulePage() {
  const { moduleId } = useParams();
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [newRow, setNewRow] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [selectFilters, setSelectFilters] = useState({}); // {colKey: value}
  const [overdueOnly, setOverdueOnly] = useState(false);

  const { data: module } = useQuery({
    queryKey: ["module", moduleId],
    queryFn: () => fetchModule(moduleId),
  });
  const { data: records = [], isLoading } = useQuery({
    queryKey: ["records", moduleId],
    queryFn: () => fetchRecords(moduleId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["records", moduleId] });
    qc.invalidateQueries({ queryKey: ["summary"] });
  };

  const createMut = useMutation({
    mutationFn: (data) => createRecord(moduleId, data),
    onSuccess: () => {
      toast.success("Baris ditambahkan");
      setNewRow(null);
      invalidate();
    },
    onError: () => toast.error("Gagal menambahkan baris"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateRecord(id, data),
    onSuccess: () => {
      toast.success("Perubahan disimpan");
      setEditingId(null);
      invalidate();
    },
    onError: () => toast.error("Gagal menyimpan"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => deleteRecord(id),
    onSuccess: () => {
      toast.success("Baris dihapus");
      invalidate();
    },
    onError: () => toast.error("Gagal menghapus"),
  });

  const clearMut = useMutation({
    mutationFn: () => clearModule(moduleId),
    onSuccess: (r) => {
      toast.success(`${r.deleted} baris dikosongkan`);
      setConfirmClear(false);
      invalidate();
    },
    onError: () => toast.error("Gagal mengosongkan modul"),
  });

  const filtered = useMemo(() => {
    let rows = records;
    // apply select filters
    for (const [k, v] of Object.entries(selectFilters)) {
      if (!v) continue;
      rows = rows.filter((r) => (r.data?.[k] ?? "") === v);
    }
    if (overdueOnly) {
      rows = rows.filter((r) => computeOverdue(r).overdue);
    }
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r.data || {}).some((val) =>
        String(val ?? "").toLowerCase().includes(q)
      )
    );
  }, [records, search, selectFilters, overdueOnly]);

  // count of overdue rows (before filtering) — for chip badge
  const overdueCount = useMemo(
    () => records.filter((r) => computeOverdue(r).overdue).length,
    [records]
  );

  if (!module) {
    return (
      <div className="p-10 text-slate-400">Memuat modul...</div>
    );
  }

  const cols = module.columns;
  const selectCols = cols.filter((c) => c.type === "select");

  // Counts per option for each select column (before search)
  const selectCounts = {};
  selectCols.forEach((c) => {
    selectCounts[c.key] = {};
    records.forEach((r) => {
      const v = r.data?.[c.key] ?? "";
      selectCounts[c.key][v] = (selectCounts[c.key][v] || 0) + 1;
    });
  });

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditRow({ ...r.data });
    setNewRow(null);
  };

  const startNew = () => {
    const empty = {};
    cols.forEach((c) => (empty[c.key] = c.type === "number" ? "" : ""));
    setNewRow(empty);
    setEditingId(null);
  };

  const saveNew = () => {
    const cleaned = {};
    Object.keys(newRow).forEach((k) => {
      const v = newRow[k];
      const col = cols.find((c) => c.key === k);
      if (v === "" || v === null || v === undefined) return;
      cleaned[k] = col?.type === "number" ? Number(v) : v;
    });
    if (Object.keys(cleaned).length === 0) {
      toast.error("Isi minimal satu kolom");
      return;
    }
    createMut.mutate(cleaned);
  };

  const saveEdit = () => {
    const cleaned = {};
    Object.keys(editRow).forEach((k) => {
      const v = editRow[k];
      const col = cols.find((c) => c.key === k);
      if (v === "" || v === null || v === undefined) return;
      cleaned[k] = col?.type === "number" ? Number(v) : v;
    });
    updateMut.mutate({ id: editingId, data: cleaned });
  };

  const download = (format) => {
    const url = exportUrl(moduleId, format);
    window.open(url, "_blank");
  };

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400 mb-2">
            Modul · {moduleId}
          </div>
          <h1
            data-testid="module-title"
            className="font-display font-black text-3xl lg:text-4xl tracking-tight text-slate-900"
          >
            {module.name}
          </h1>
          <p className="text-slate-500 mt-2 max-w-2xl">{module.description}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="btn-ai-analyze"
            onClick={() => window.__openAi && window.__openAi(moduleId)}
            className="btn-sharp inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 hover:bg-[#0047FF] transition-colors"
          >
            <Sparkles className="w-4 h-4" /> AI Analisis
          </button>
          <button
            data-testid="btn-upload"
            onClick={() => setUploadOpen(true)}
            className="btn-sharp inline-flex items-center gap-2 bg-[#0047FF] text-white text-sm font-semibold px-4 py-2.5 hover:bg-[#0033CC] transition-colors"
          >
            <Upload className="w-4 h-4" /> Unggah Excel
          </button>
          <button
            data-testid="btn-add"
            onClick={startNew}
            className="btn-sharp inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2.5 hover:border-slate-900 transition-colors"
          >
            <Plus className="w-4 h-4" /> Tambah Baris
          </button>
          <button
            data-testid="btn-export-xlsx"
            onClick={() => download("xlsx")}
            className="btn-sharp inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2.5 hover:border-slate-900 transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" /> Ekspor .xlsx
          </button>
          <button
            data-testid="btn-export-csv"
            onClick={() => download("csv")}
            className="btn-sharp inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2.5 hover:border-slate-900 transition-colors"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-6">
        <DataChart module={module} records={records} />
      </div>

      {/* Table controls */}
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari baris..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#0047FF] btn-sharp"
            />
          </div>

          {/* Filter chips per select column */}
          {selectCols.map((c) => {
            const active = selectFilters[c.key] || "";
            const opts = c.options || [];
            return (
              <div
                key={c.key}
                data-testid={`filter-group-${c.key}`}
                className="flex items-center gap-1 border border-slate-200 bg-white p-1"
              >
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400 px-2">
                  {c.label}:
                </span>
                <button
                  data-testid={`filter-${c.key}-all`}
                  onClick={() =>
                    setSelectFilters((f) => {
                      const next = { ...f };
                      delete next[c.key];
                      return next;
                    })
                  }
                  className={`btn-sharp text-[11px] font-semibold px-2 py-1 ${
                    !active
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  Semua ({records.length})
                </button>
                {opts.map((o) => {
                  const count = selectCounts[c.key]?.[o] || 0;
                  return (
                    <button
                      key={o}
                      data-testid={`filter-${c.key}-${o}`}
                      onClick={() => setSelectFilters((f) => ({ ...f, [c.key]: o }))}
                      className={`btn-sharp text-[11px] font-semibold px-2 py-1 ${
                        active === o
                          ? "bg-[#0047FF] text-white"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {o} ({count})
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Overdue toggle (only for modules with attachments = SPD) */}
          {module.has_attachments && (
            <button
              data-testid="filter-overdue"
              onClick={() => setOverdueOnly((v) => !v)}
              className={`btn-sharp inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 border ${
                overdueOnly
                  ? "bg-rose-600 text-white border-rose-600"
                  : overdueCount > 0
                  ? "bg-white text-rose-700 border-rose-300 hover:border-rose-500"
                  : "bg-white text-slate-400 border-slate-200 cursor-not-allowed"
              }`}
              disabled={overdueCount === 0}
              title={overdueCount === 0 ? "Tidak ada SPD overdue" : "Filter overdue"}
            >
              <Clock className="w-3 h-3" />
              Overdue ({overdueCount})
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="mono text-[11px] uppercase tracking-widest text-slate-400">
            {filtered.length} / {records.length} baris
          </span>
          <button
            data-testid="btn-clear"
            onClick={() => setConfirmClear(true)}
            className="btn-sharp text-xs font-semibold text-rose-600 hover:text-rose-700 border border-rose-200 hover:border-rose-400 px-3 py-2"
          >
            Kosongkan Modul
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full text-sm tabular">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className="px-4 py-3 text-left whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right w-32">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {newRow && (
                <tr className="bg-blue-50/50">
                  {cols.map((c) => (
                    <td key={c.key} className="px-4 py-2">
                      {c.type === "select" ? (
                        <select
                          data-testid={`new-${c.key}`}
                          value={newRow[c.key] ?? ""}
                          onChange={(e) =>
                            setNewRow({ ...newRow, [c.key]: e.target.value })
                          }
                          className="w-full px-2 py-1.5 border border-slate-300 text-sm focus:outline-none focus:border-[#0047FF] bg-white"
                        >
                          <option value="">—</option>
                          {(c.options || []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          data-testid={`new-${c.key}`}
                          type={c.type === "number" ? "number" : "text"}
                          value={newRow[c.key] ?? ""}
                          onChange={(e) =>
                            setNewRow({ ...newRow, [c.key]: e.target.value })
                          }
                          placeholder={c.label}
                          className="w-full px-2 py-1.5 border border-slate-300 text-sm focus:outline-none focus:border-[#0047FF]"
                        />
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid="btn-save-new"
                        onClick={saveNew}
                        className="p-1.5 bg-[#0047FF] text-white hover:bg-[#0033CC]"
                        title="Simpan"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setNewRow(null)}
                        className="p-1.5 border border-slate-300 hover:border-slate-500"
                        title="Batal"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {isLoading && (
                <tr>
                  <td colSpan={cols.length + 1} className="px-4 py-16 text-center text-slate-400">
                    Memuat data...
                  </td>
                </tr>
              )}

              {!isLoading && filtered.length === 0 && !newRow && (
                <tr>
                  <td colSpan={cols.length + 1} className="px-4 py-16 text-center">
                    <div className="text-slate-400 text-sm">
                      Belum ada data. Klik <b>Unggah Excel</b> atau <b>Tambah Baris</b>.
                    </div>
                  </td>
                </tr>
              )}

              {filtered.map((r) => {
                const isEditing = editingId === r.id;
                const attCount = (r.attachments || []).length;
                const overdue = module.has_attachments ? computeOverdue(r) : { overdue: false };
                return (
                  <tr
                    key={r.id}
                    className={`hover:bg-slate-50 ${
                      overdue.overdue ? "border-l-4 border-l-rose-500 bg-rose-50/30" : ""
                    }`}
                    data-testid={`row-${r.id}`}
                  >
                    {cols.map((c) => (
                      <td key={c.key} className="px-4 py-2.5 align-middle">
                        {isEditing ? (
                          c.type === "select" ? (
                            <select
                              data-testid={`edit-${c.key}`}
                              value={editRow[c.key] ?? ""}
                              onChange={(e) =>
                                setEditRow({ ...editRow, [c.key]: e.target.value })
                              }
                              className="w-full px-2 py-1.5 border border-slate-300 text-sm focus:outline-none focus:border-[#0047FF] bg-white"
                            >
                              <option value="">—</option>
                              {(c.options || []).map((o) => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              data-testid={`edit-${c.key}`}
                              type={c.type === "number" ? "number" : "text"}
                              value={editRow[c.key] ?? ""}
                              onChange={(e) =>
                                setEditRow({ ...editRow, [c.key]: e.target.value })
                              }
                              className="w-full px-2 py-1.5 border border-slate-300 text-sm focus:outline-none focus:border-[#0047FF]"
                            />
                          )
                        ) : (
                          <>
                            <CellDisplay value={r.data?.[c.key]} col={c} />
                            {c.key === "tanggal" && overdue.overdue && (
                              <span
                                data-testid={`overdue-badge-${r.id}`}
                                className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-rose-600 text-white px-1.5 py-0.5"
                                title={`Overdue ${overdue.daysLate} hari`}
                              >
                                <Clock className="w-2.5 h-2.5" />
                                {overdue.daysLate}h
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isEditing ? (
                          <>
                            <button
                              data-testid="btn-save-edit"
                              onClick={saveEdit}
                              className="p-1.5 bg-[#0047FF] text-white hover:bg-[#0033CC]"
                              title="Simpan"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="p-1.5 border border-slate-300 hover:border-slate-500"
                              title="Batal"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            {module.has_attachments && (
                              <button
                                data-testid={`btn-detail-${r.id}`}
                                onClick={() => setDetailRecord(r)}
                                className="relative p-1.5 border border-slate-300 hover:border-[#0047FF] hover:text-[#0047FF]"
                                title="Detail & Lampiran"
                              >
                                <FolderOpen className="w-4 h-4 text-slate-600" />
                                {attCount > 0 && (
                                  <span className="absolute -top-1 -right-1 bg-[#0047FF] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                    {attCount > 9 ? "9+" : attCount}
                                  </span>
                                )}
                              </button>
                            )}
                            <button
                              data-testid={`btn-edit-${r.id}`}
                              onClick={() => startEdit(r)}
                              className="p-1.5 border border-slate-300 hover:border-slate-900"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4 text-slate-600" />
                            </button>
                            <button
                              data-testid={`btn-delete-${r.id}`}
                              onClick={() => {
                                if (window.confirm("Hapus baris ini?"))
                                  deleteMut.mutate(r.id);
                              }}
                              className="p-1.5 border border-slate-300 hover:border-rose-500 hover:text-rose-500"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4 text-slate-600" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload dialog */}
      {uploadOpen && (
        <UploadDialog
          module={module}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => {
            setUploadOpen(false);
            invalidate();
          }}
        />
      )}

      {/* SPD detail modal */}
      {detailRecord && (
        <SpdDetailModal
          record={detailRecord}
          onClose={() => {
            setDetailRecord(null);
            invalidate();
          }}
        />
      )}


      {/* Confirm clear dialog */}
      {confirmClear && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-display font-black text-lg tracking-tight">
                  Kosongkan Modul?
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Semua {records.length} baris pada modul <b>{module.name}</b> akan
                  dihapus permanen. Tindakan ini tidak dapat dibatalkan.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmClear(false)}
                className="btn-sharp px-4 py-2 border border-slate-300 text-sm font-semibold hover:border-slate-500"
              >
                Batal
              </button>
              <button
                data-testid="btn-confirm-clear"
                onClick={() => clearMut.mutate()}
                className="btn-sharp px-4 py-2 bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"
              >
                Ya, kosongkan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CellDisplay({ value, col }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-slate-300">—</span>;
  }
  if (col.type === "select") {
    const styles = {
      Draft: "bg-slate-100 text-slate-700 border-slate-300",
      Berjalan: "bg-amber-50 text-amber-800 border-amber-300",
      Selesai: "bg-emerald-50 text-emerald-800 border-emerald-300",
      Batal: "bg-rose-50 text-rose-800 border-rose-300",
    };
    const cls = styles[value] || "bg-slate-100 text-slate-700 border-slate-300";
    return (
      <span className={`inline-block text-[11px] font-semibold px-2 py-1 border ${cls}`}>
        {value}
      </span>
    );
  }
  if (col.type === "number") {
    const n = Number(value);
    if (!Number.isNaN(n)) {
      if (
        col.key.includes("net_penjualan") ||
        col.key.includes("total_transaksi")
      ) {
        return <span className="font-medium">Rp {n.toLocaleString("id-ID")}</span>;
      }
      return <span>{n.toLocaleString("id-ID")}</span>;
    }
  }
  return <span>{String(value)}</span>;
}
